/**
 * Minimaler ZIP-Schreiber für den Browser.
 *
 * Warum keine ZIP-Bibliothek: Die einzige Aufgabe hier ist, fertige PDFs zu
 * bündeln. PDF-Inhalte sind intern bereits komprimiert, eine zweite
 * Kompression bringt praktisch nichts. Damit reicht die ZIP-Speichermethode
 * („stored“, Methode 0) vollständig aus – und das Projekt bleibt ohne
 * zusätzliche Abhängigkeit.
 *
 * Erzeugt wird das klassische ZIP-Format (kein ZIP64). Das genügt: eine ZIP
 * mit IBN-Protokollen bleibt weit unter 4 GB und unter 65.535 Einträgen.
 */

/** Eine Datei im Archiv. */
export interface ZipEintrag {
  /** Dateiname im Archiv, inklusive Endung. */
  name: string;
  daten: Uint8Array;
}

// Signaturen laut ZIP-Spezifikation (APPNOTE 4.3).
const SIG_LOCAL = 0x04034b50;
const SIG_CENTRAL = 0x02014b50;
const SIG_EOCD = 0x06054b50;

/**
 * Bit 11 des General-Purpose-Flags: der Dateiname liegt als UTF-8 vor.
 * Ohne dieses Bit zerlegen Windows-Packer Umlaute in einem Dateinamen.
 */
const FLAG_UTF8 = 0x0800;

/** Kompression 0 = „stored“: die Daten stehen roh im Archiv. */
const METHODE_GESPEICHERT = 0;

/** CRC-32-Tabelle. Einmal aufgebaut, danach für jede Datei wiederverwendet. */
const CRC_TABELLE: Uint32Array = (() => {
  const tabelle = new Uint32Array(256);

  for (let i = 0; i < 256; i++) {
    let wert = i;

    for (let bit = 0; bit < 8; bit++) {
      wert = (wert & 1) ? (0xedb88320 ^ (wert >>> 1)) : (wert >>> 1);
    }

    tabelle[i] = wert >>> 0;
  }

  return tabelle;
})();

function crc32(daten: Uint8Array): number {
  let crc = 0xffffffff;

  for (let i = 0; i < daten.length; i++) {
    crc = CRC_TABELLE[(crc ^ daten[i]) & 0xff] ^ (crc >>> 8);
  }

  return (crc ^ 0xffffffff) >>> 0;
}

/**
 * Zeitstempel im MS-DOS-Format, wie ZIP ihn erwartet.
 * Sekunden haben dort nur 5 Bit – die Auflösung beträgt also 2 Sekunden.
 */
function dosZeit(datum: Date): { zeit: number; datum: number } {
  return {
    zeit:
      (datum.getHours() << 11)
      | (datum.getMinutes() << 5)
      | (Math.floor(datum.getSeconds() / 2)),
    datum:
      ((Math.max(1980, datum.getFullYear()) - 1980) << 9)
      | ((datum.getMonth() + 1) << 5)
      | datum.getDate()
  };
}

/**
 * Entfernt aus einem Dateinamen, was in einem Archiv nichts zu suchen hat.
 * Pfadtrenner und Doppelpunkte machen aus einem Namen sonst eine
 * Verzeichnisstruktur oder einen unter Windows ungültigen Eintrag.
 */
export function sichererDateiName(name: string, ersatz = 'Datei'): string {
  const bereinigt = (name ?? '')
    .replace(/[\\/:*?"<>|\r\n\t]+/g, '_')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^\.+/, '');

  return bereinigt.length > 0 ? bereinigt.slice(0, 150) : ersatz;
}

/**
 * Sorgt dafür, dass jeder Name im Archiv genau einmal vorkommt.
 * Zwei Messstellen ohne Kennzeichnung hießen sonst gleich – der zweite
 * Eintrag wäre beim Entpacken nicht mehr auffindbar.
 */
function eindeutigeNamen(eintraege: ZipEintrag[]): ZipEintrag[] {
  const vergeben = new Map<string, number>();

  return eintraege.map(eintrag => {
    const schluessel = eintrag.name.toLowerCase();
    const bisher = vergeben.get(schluessel) ?? 0;

    vergeben.set(schluessel, bisher + 1);

    if (bisher === 0) {
      return eintrag;
    }

    const punkt = eintrag.name.lastIndexOf('.');
    const basis = punkt > 0 ? eintrag.name.slice(0, punkt) : eintrag.name;
    const endung = punkt > 0 ? eintrag.name.slice(punkt) : '';

    return { ...eintrag, name: `${basis} (${bisher + 1})${endung}` };
  });
}

/** Packt die Einträge in ein ZIP-Archiv. */
export function createZipBlob(eintraege: ZipEintrag[]): Blob {
  const jetzt = dosZeit(new Date());
  const kodierer = new TextEncoder();

  const vorbereitet = eindeutigeNamen(eintraege).map(eintrag => ({
    name: kodierer.encode(eintrag.name),
    daten: eintrag.daten,
    crc: crc32(eintrag.daten)
  }));

  const lokaleGroesse = vorbereitet.reduce(
    (summe, e) => summe + 30 + e.name.length + e.daten.length,
    0
  );

  const zentralGroesse = vorbereitet.reduce(
    (summe, e) => summe + 46 + e.name.length,
    0
  );

  const puffer = new ArrayBuffer(lokaleGroesse + zentralGroesse + 22);
  const sicht = new DataView(puffer);
  const bytes = new Uint8Array(puffer);

  let position = 0;
  const versatz: number[] = [];

  // ── Lokale Dateiköpfe samt Daten ──
  for (const eintrag of vorbereitet) {
    versatz.push(position);

    sicht.setUint32(position, SIG_LOCAL, true);
    sicht.setUint16(position + 4, 20, true);                    // benötigte Version 2.0
    sicht.setUint16(position + 6, FLAG_UTF8, true);
    sicht.setUint16(position + 8, METHODE_GESPEICHERT, true);
    sicht.setUint16(position + 10, jetzt.zeit, true);
    sicht.setUint16(position + 12, jetzt.datum, true);
    sicht.setUint32(position + 14, eintrag.crc, true);
    sicht.setUint32(position + 18, eintrag.daten.length, true); // komprimierte Größe
    sicht.setUint32(position + 22, eintrag.daten.length, true); // Originalgröße
    sicht.setUint16(position + 26, eintrag.name.length, true);
    sicht.setUint16(position + 28, 0, true);                    // kein Extra-Feld
    position += 30;

    bytes.set(eintrag.name, position);
    position += eintrag.name.length;

    bytes.set(eintrag.daten, position);
    position += eintrag.daten.length;
  }

  // ── Zentrales Verzeichnis ──
  const zentralStart = position;

  vorbereitet.forEach((eintrag, index) => {
    sicht.setUint32(position, SIG_CENTRAL, true);
    sicht.setUint16(position + 4, 20, true);                    // erzeugt mit Version 2.0
    sicht.setUint16(position + 6, 20, true);                    // benötigte Version 2.0
    sicht.setUint16(position + 8, FLAG_UTF8, true);
    sicht.setUint16(position + 10, METHODE_GESPEICHERT, true);
    sicht.setUint16(position + 12, jetzt.zeit, true);
    sicht.setUint16(position + 14, jetzt.datum, true);
    sicht.setUint32(position + 16, eintrag.crc, true);
    sicht.setUint32(position + 20, eintrag.daten.length, true);
    sicht.setUint32(position + 24, eintrag.daten.length, true);
    sicht.setUint16(position + 28, eintrag.name.length, true);
    sicht.setUint16(position + 30, 0, true);                    // kein Extra-Feld
    sicht.setUint16(position + 32, 0, true);                    // kein Kommentar
    sicht.setUint16(position + 34, 0, true);                    // Datenträger 0
    sicht.setUint16(position + 36, 0, true);                    // interne Attribute
    sicht.setUint32(position + 38, 0, true);                    // externe Attribute
    sicht.setUint32(position + 42, versatz[index], true);
    position += 46;

    bytes.set(eintrag.name, position);
    position += eintrag.name.length;
  });

  // ── Abschluss des zentralen Verzeichnisses ──
  sicht.setUint32(position, SIG_EOCD, true);
  sicht.setUint16(position + 4, 0, true);                       // Datenträger 0
  sicht.setUint16(position + 6, 0, true);                       // Verzeichnis auf Datenträger 0
  sicht.setUint16(position + 8, vorbereitet.length, true);
  sicht.setUint16(position + 10, vorbereitet.length, true);
  sicht.setUint32(position + 12, position - zentralStart, true);
  sicht.setUint32(position + 16, zentralStart, true);
  sicht.setUint16(position + 20, 0, true);                      // kein Kommentar

  return new Blob([puffer], { type: 'application/zip' });
}

/** Packt die Einträge und startet den Download der ZIP-Datei. */
export function downloadZip(dateiName: string, eintraege: ZipEintrag[]): void {
  const url = URL.createObjectURL(createZipBlob(eintraege));

  const link = document.createElement('a');
  link.href = url;
  link.download = dateiName.toLowerCase().endsWith('.zip') ? dateiName : `${dateiName}.zip`;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Der Browser braucht den Blob nur bis zum Start des Downloads.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
